import { useState } from "react";
import { RichField } from "@/components/richtext/RichField";
import { groupIdForKey, KVP_GROUP_DOCS, KVP_KEY_DOCS } from "./kvpDocs";
import { graphIdForKey, highlightForKey, KvpGraph } from "./kvpGraphs";
import { EXPECTED_KVP_KEY_SET } from "./kvpKeyRegistry";

/**
 * Shown above the editor of a selected entry: its one-line note and its graph
 * with this entry highlighted. `field` narrows the highlight to one field of a
 * multi-field record (the default prompt set).
 */
export const KvpEntryExplainer = ({ keyPath, field }: { keyPath: string; field?: string | null }) => {
    const graph = graphIdForKey(keyPath);
    const note = KVP_KEY_DOCS[keyPath]?.note;
    if (!graph && !note) return null;
    return (
        <div className="panel">
            {note && <p className="help-text">{note}</p>}
            {graph && <KvpGraph graph={graph} highlight={highlightForKey(keyPath, field)} />}
        </div>
    );
};

/** Shown on its own when a branch of the tree (system_prompt, tool, ...) is clicked. */
export const KvpGroupOverview = ({
    path,
    rows,
    persistedKeys,
    onOpenKey,
}: {
    path: string;
    rows: { key: string }[];
    persistedKeys: Set<string>;
    onOpenKey: (key: string) => void;
}) => {
    const [hovered, setHovered] = useState<string | null>(null);
    const groupId = groupIdForKey(path);
    if (!groupId) return null;
    const group = KVP_GROUP_DOCS[groupId];
    const prefix = `${path}.`;
    const children = rows.map((row) => row.key).filter((key) => key.startsWith(prefix));
    const graph = graphIdForKey(prefix);

    return (
        <div className="flex flex-col gap-[.75rem]">
            <div className="inspector-header">
                <p className="eyebrow">{path}</p>
                <p className="h2 truncate">{group.title}</p>
            </div>
            <div className="panel">
                <p className="help-text">{group.summary}</p>
                {graph && (
                    <KvpGraph
                        graph={hovered ? graphIdForKey(hovered) ?? graph : graph}
                        highlight={hovered ? highlightForKey(hovered) : null}
                    />
                )}
            </div>
            <div className="flex flex-col gap-[.35rem]" onMouseLeave={() => setHovered(null)}>
                {children.map((key) => {
                    const doc = KVP_KEY_DOCS[key];
                    const missing = EXPECTED_KVP_KEY_SET.has(key) && !persistedKeys.has(key);
                    return (
                        <div
                            key={key}
                            className="list-card list-card-interactive"
                            onClick={() => onOpenKey(key)}
                            onMouseEnter={() => setHovered(key)}
                        >
                            <div className="flex flex-col gap-[.2rem] min-w-0">
                                <div className="flex flex-row items-baseline gap-[.5rem] min-w-0">
                                    <span className="code-chip code-chip-muted truncate">{key}</span>
                                    {doc?.name && <span className="l truncate">{doc.name}</span>}
                                    {missing && <span className="code-chip code-chip-danger">not saved yet</span>}
                                </div>
                                {doc && <p className="help-text">{doc.note}</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * One long-text field: markdown richtext for prompts (in the same editing band as
 * entry bodies elsewhere in the app), a plain textarea otherwise.
 */
export const KvpField = ({
    label,
    value,
    onChange,
    richText,
    minHeightRem = 20,
    hint,
    onFocus,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    richText: boolean;
    minHeightRem?: number;
    hint?: string;
    onFocus?: () => void;
}) => richText ? (
    <div className="entry-body-editing" onFocusCapture={onFocus} onMouseEnter={onFocus}>
        <div className="entry-field entry-field-rich">
            <small className="field-label">{label}</small>
            {hint && <p className="help-text">{hint}</p>}
            <RichField value={value} onChange={onChange} minHeightRem={minHeightRem} />
        </div>
    </div>
) : (
    <div className="field flex-1 min-h-0" onFocusCapture={onFocus} onMouseEnter={onFocus}>
        <small className="field-label">{label}</small>
        {hint && <p className="help-text">{hint}</p>}
        <textarea
            className="textarea input-mono flex-1"
            style={{ minHeight: `${Math.min(minHeightRem, 12)}rem` }}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    </div>
);
